'use client';

import React from 'react';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { formatNumber } from '@/utils/coins/formatNumber';
import moment from 'jalali-moment';
import { CoinProps } from '@/types/coins';
import { formatTomanPrice } from '@/utils/coins/bubble';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { isUsdQuoted } from '@/utils/coins/quote';
import { faUsd, jalaliDate as jalaliDay, tehranTime } from '@/utils/gold/mazanehFormat';
import { toPersianNumber } from '@/utils/persianUtils';
import type { CryptoDayRange } from '@/utils/coins/fetchAditionalSymbolSingleData';
import { getPriceChangeDetails } from '@/utils/coins/formatNumber';

interface SingleCoinTableStatusProps extends CoinProps {
    /** Today's high and low (Tehran day) from the daily candles; gold & fiat only */
    today?: { high: number; low: number; quoteCurrency?: string };
    /** Crypto: 24h change in percent (the page's `change24h`) */
    change24hCrypto?: number;
    /** Crypto: lowest/highest price of the last 24 hours */
    cryptoDayRange?: CryptoDayRange;
    /** Crypto: best toman prices in the Iranian exchanges list (buy = lowest ask, sell = highest bid) */
    bestExchangePrices?: { buy: number; sell: number };
}

/**
 * Market facts of one symbol. Rendered on the server (price, 24h change, today's range and the
 * update time are in the HTML) and kept live by the symbol's shared socket in the browser.
 */
const SingleCoinTableStatus: React.FC<SingleCoinTableStatusProps> = ({
    marketCap,
    name,
    nameFa,
    allTimeHigh,
    supply,
    price,
    rank,
    dominance,
    listedAt,
    type,
    change_24h,
    change_toman,
    symbol,
    price_usd,
    quote_currency,
    price_updated_at,
    today,
    change24hCrypto,
    cryptoDayRange,
    bestExchangePrices,
    asset_type,
    typeId,
}) => {
    const isTomanAsset = type === 'gold' || type === 'currency';
    const usdQuoted = isTomanAsset && isUsdQuoted({ price_usd, quote_currency });
    // Live values from the symbol's shared socket (the page header already holds that connection)
    const { livePriceToman, livePriceUsdt, liveChange24h, liveChangeToman, livePriceUpdatedAt } =
        useCoinPriceSocket(
            isTomanAsset ? symbol || '' : '',
            usdQuoted ? price_usd || 0 : 0,
            isTomanAsset && typeof price === 'number' ? price : 0,
            isTomanAsset ? (type as 'gold' | 'currency') : 'crypto',
            price_updated_at,
        );

    if (isTomanAsset) {
        const tomanPriceValue = livePriceToman || (typeof price === 'number' ? price : 0);
        const change24hVal = liveChange24h ?? (change_24h || 0);
        const changeTomanVal = liveChangeToman ?? (change_toman || 0);
        // Today's range is in the candles' currency (dollars for the world gold ounce)
        const rangeInUsd = (today?.quoteCurrency || '').toUpperCase() === 'USD';
        const rangeUnit = rangeInUsd ? 'دلار' : 'تومان';
        const formatRange = (n: number) => (rangeInUsd ? faUsd(n) : formatTomanPrice(n, symbol));
        const updatedMs = Date.parse(livePriceUpdatedAt || price_updated_at || '');
        const updatedAt = Number.isFinite(updatedMs) ? new Date(updatedMs) : null;

        const goldCurrencyStatus = [
            // Dollar-quoted gold (the world gold ounce): the dollar price above the toman one
            ...(usdQuoted
                ? [
                      {
                          header: `قیمت ${nameFa || name} (دلار)`,
                          value: (
                              <span className='font-yekanBakh font-bold' dir='ltr'>
                                  $
                                  {formatNumber(
                                      livePriceUsdt || price_usd || 0,
                                      false,
                                      true,
                                      'USD',
                                      true,
                                  )}
                              </span>
                          ),
                      },
                  ]
                : []),
            {
                header: `قیمت ${nameFa || name} (تومان)`,
                value: (
                    <span className='font-yekanBakh font-bold'>
                        <bdi dir='ltr'>{formatTomanPrice(tomanPriceValue, symbol)}</bdi> تومان
                    </span>
                ),
            },
            {
                header: 'تغییر ۲۴ ساعت',
                value: (
                    <div className='inline-flex items-center gap-2 font-yekanBakh font-bold text-xs'>
                        <span
                            dir='ltr'
                            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-xs font-bold tabular-nums ${
                                change24hVal > 0
                                    ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                                    : change24hVal < 0
                                      ? 'bg-rose-500/10 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400'
                                      : 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400'
                            }`}
                        >
                            {change24hVal > 0
                                ? `+${change24hVal.toFixed(2)}`
                                : `${change24hVal.toFixed(2)}`}
                            %
                        </span>
                        {changeTomanVal !== 0 && (
                            <span
                                className={`text-xs ${
                                    changeTomanVal > 0
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : 'text-rose-600 dark:text-rose-400'
                                }`}
                            >
                                <bdi dir='ltr'>
                                    {changeTomanVal > 0 ? '+' : '-'}
                                    {formatNumber(
                                        Math.abs(changeTomanVal),
                                        false,
                                        true,
                                        'TMN',
                                        true,
                                    )}
                                </bdi>{' '}
                                تومان
                            </span>
                        )}
                    </div>
                ),
            },
            ...(today && today.high > 0 && today.low > 0
                ? [
                      {
                          header: 'بیشترین امروز',
                          value: (
                              <span className='font-yekanBakh font-bold'>
                                  <bdi dir='ltr'>{formatRange(today.high)}</bdi> {rangeUnit}
                              </span>
                          ),
                      },
                      {
                          header: 'کمترین امروز',
                          value: (
                              <span className='font-yekanBakh font-bold'>
                                  <bdi dir='ltr'>{formatRange(today.low)}</bdi> {rangeUnit}
                              </span>
                          ),
                      },
                  ]
                : []),
            ...(updatedAt
                ? [
                      {
                          header: 'به‌روزرسانی',
                          value: (
                              <time
                                  dateTime={updatedAt.toISOString()}
                                  suppressHydrationWarning
                                  className='font-yekanBakh text-xs text-primary/70 dark:text-white/70'
                              >
                                  {tehranTime(updatedAt, false)} — {jalaliDay(updatedAt)}
                              </time>
                          ),
                      },
                  ]
                : []),
        ];

        return (
            <Table>
                <TableBody>
                    {goldCurrencyStatus.map((stats, index) => (
                        <TableRow
                            key={stats.header}
                            className={`!flex justify-between items-center !px-0 transition-all w-full ${
                                index === goldCurrencyStatus.length - 1
                                    ? '!border-b-0'
                                    : '!border-b border-solid border-primary/10 dark:border-white/10'
                            }`}
                        >
                            <TableCell className='text-xs text-primary/70 dark:text-white/70 py-3 whitespace-nowrap shrink-0'>
                                {stats.header}
                            </TableCell>
                            <TableCell className='text-end text-xs font-yekanBakh font-normal py-3'>
                                {stats.value}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    // Fixed Tehran offset: this table is server-rendered now, and the server's UTC day differs
    // from the visitor's Tehran day in the evening (hydration mismatch otherwise)
    const jalaliDate =
        moment
            .unix(allTimeHigh?.timestamp || 0)
            .utcOffset('+03:30')
            .format('jYYYY/jM/jD') || '';
    const listedAtData =
        moment
            .unix(listedAt || 0)
            .utcOffset('+03:30')
            .format('jYYYY/jM/jD') || '';

    const tomanPriceValue =
        typeof price === 'number'
            ? price
            : typeof price === 'object' && price?.price_buy && price?.usdt_price
              ? price.price_buy * price.usdt_price
              : typeof price === 'object' && price?.ir_price_buy
                ? price.ir_price_buy
                : 0;

    const fa = toPersianNumber;
    // Stablecoins (USDT…) are priced in toman on this site; other coins in dollars
    const tomanFirst = symbol === 'USDT' || asset_type === 'stableCoin' || typeId === 2;
    const toman = (n: number) => formatNumber(n, false, true, 'TMN', true);
    const usd = (n: number) => fa(formatNumber(n, false, true, 'USD', true));
    const change = getPriceChangeDetails(change24hCrypto || 0);

    const status = [
        ...(bestExchangePrices?.buy
            ? [
                  {
                      header: 'بهترین قیمت خرید در صرافی‌ها',
                      value: (
                          <span className='font-yekanBakh font-bold'>
                              <bdi dir='ltr'>{toman(bestExchangePrices.buy)}</bdi> تومان
                          </span>
                      ),
                  },
              ]
            : []),
        ...(bestExchangePrices?.sell
            ? [
                  {
                      header: 'بهترین قیمت فروش در صرافی‌ها',
                      value: (
                          <span className='font-yekanBakh font-bold'>
                              <bdi dir='ltr'>{toman(bestExchangePrices.sell)}</bdi> تومان
                          </span>
                      ),
                  },
              ]
            : []),
        ...(change24hCrypto !== undefined
            ? [
                  {
                      header: 'تغییر ۲۴ ساعت',
                      value: (
                          <span
                              className='font-yekanBakh font-bold'
                              style={{ color: change.color }}
                          >
                              {change.text ? `${change.text} ${change.onlyText}` : change.onlyText}
                          </span>
                      ),
                  },
              ]
            : []),
        ...(cryptoDayRange
            ? [
                  {
                      header: 'بازهٔ قیمت ۲۴ ساعت',
                      value: (
                          <span className='font-yekanBakh font-bold'>
                              {tomanFirst ? (
                                  <>
                                      <bdi dir='ltr'>{toman(cryptoDayRange.lowToman)}</bdi> تا{' '}
                                      <bdi dir='ltr'>{toman(cryptoDayRange.highToman)}</bdi> تومان
                                  </>
                              ) : (
                                  <>
                                      <bdi dir='ltr'>${usd(cryptoDayRange.lowUsd)}</bdi> تا{' '}
                                      <bdi dir='ltr'>${usd(cryptoDayRange.highUsd)}</bdi>
                                  </>
                              )}
                          </span>
                      ),
                  },
              ]
            : []),
        {
            header: `قیمت ${nameFa || name} (تومان)`,
            value: (
                <span className={'font-yekanBakh font-bold'}>
                    <bdi dir='ltr'>{formatTomanPrice(tomanPriceValue, symbol)}</bdi> تومان
                </span>
            ),
        },
        {
            header: 'رتبه در بازار',
            value: `#${fa(rank)}`,
        },
        {
            header: 'حجم کل بازار',
            value: `$${formatNumber(marketCap || 0, false, true, 'TMN', true)}`,
        },
        {
            header: 'دامیننس',
            value: `٪${fa(dominance?.toFixed(2) || '0')}`,
        },
        {
            header: 'تاریخ لیست شده',
            value: fa(listedAtData),
        },
        {
            header: 'کوین در گردش',
            value: formatNumber(supply?.circulating || 0, false, true, 'TMN', true),
        },
        {
            header: 'عرضه کل',
            value: formatNumber(supply?.total || 0, false, true, 'TMN', true),
        },
        {
            header: 'بالاترین قیمت',
            value: (
                <div className={'inline-flex flex-row-reverse items-center gap-2'} dir={'ltr'}>
                    <span className={'pr-1 text-xs text-primary/40 dark:text-white/40'}>
                        ({fa(jalaliDate)})
                    </span>
                    ${fa(formatNumber(allTimeHigh?.price || 0, false, true, 'USD', false))}
                </div>
            ),
        },
    ];
    return (
        <Table>
            <TableBody>
                {status.map((stats, index) => (
                    <TableRow
                        key={stats.header}
                        className={`!flex justify-between items-center !px-0 transition-all w-full ${
                            index === status.length - 1
                                ? '!border-b-0'
                                : '!border-b border-solid border-primary/10 dark:border-white/10'
                        }`}
                    >
                        <TableCell className='text-xs text-primary/70 dark:text-white/70 py-3 whitespace-nowrap shrink-0'>
                            {stats.header}
                        </TableCell>
                        <TableCell className='text-end text-xs font-yekanBakh font-normal py-3'>
                            {stats.value}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default SingleCoinTableStatus;
